import { isPtacSystemPreference } from '@alpinehomeair/common';
import store from 'app/store';
import { AttributeType, ProjectSettingCodeType } from 'data/types';
import { isEmpty } from 'utils/helpers';

export function getProjectSetting(code: ProjectSettingCodeType) {
  const { projectSettings } = store.getState().project;
  return projectSettings.find((ps) => ps.code === code);
}

export function getProjectSettings(codes: ProjectSettingCodeType[]) {
  const { projectSettings } = store.getState().project;
  return projectSettings.filter((ps) =>
    codes.includes(ps.code as ProjectSettingCodeType)
  );
}

export function getFilteredProjectSettings(
  codes: ProjectSettingCodeType[],
  type: 'normal' | 'advanced' | 'both' = 'both',
  group?: boolean | string
) {
  const { projectSettings } = store.getState().project;
  return getProjectSettings(codes).filter(
    (a) =>
      (typeof group === 'string' ? a.group === group : true) &&
      (typeof group === 'boolean' ? !!a.group === group : true) &&
      // eslint-disable-next-line no-nested-ternary
      (type === 'normal'
        ? !a.advanced
        : type === 'advanced'
        ? a.advanced
        : true) &&
      (!a.parentAttributes ||
        a.parentAttributes.some(
          (pa) =>
            pa.value?.toString() ===
            projectSettings
              .find((ps) => ps.id === pa.attributeId)
              ?.value?.toString()
        ) ||
        a.parentAttributes.every(
          (pa) => !projectSettings.find((ps) => ps.id === pa.attributeId)
        ))
  );
}

export function getProjectSettingValue(code: ProjectSettingCodeType) {
  const { projectSettings } = store.getState().project;
  const projectSetting = projectSettings.find((ps) => ps.code === code);
  if (projectSetting?.type === 'number') {
    if (isEmpty(projectSetting.value)) {
      return undefined;
    }
    const coerced = Number(projectSetting.value);
    return Number.isNaN(coerced) ? undefined : coerced;
  }
  return projectSetting?.value;
}

export const DUCTED_PATHWAY_SYSTEM_PREFERENCES = ['Ducted', 'Help Me Decide'];

export const DUCTLESS_PATHWAY_SYSTEM_PREFERENCE = 'Ductless Mini Split';

export function isDuctedPathway() {
  return DUCTED_PATHWAY_SYSTEM_PREFERENCES.includes(
    getProjectSettingValue('system_preference')
  );
}

// STRICT ducted commitment (AHA-6681 tonnage §F) — deliberately NOT
// isDuctedPathway, which includes 'Help Me Decide' (an exploration state,
// not a commitment; the L&S skip must never arm on it). Pure half so the
// EE-footer/stepper gates can evaluate a reactive selector value; handles a
// future multi-select preference as selected ⊆ {Ducted} (non-empty).
export function isDuctedOnlySystemPreference(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0 && value.every((entry) => entry === 'Ducted');
  }
  return value === 'Ducted';
}

export function isDuctedOnlyPreference() {
  return isDuctedOnlySystemPreference(
    getProjectSettingValue('system_preference')
  );
}

// STRICT ductless commitment (AHA-6681 pathway exclusivity) — the mirror of
// isDuctedOnlySystemPreference. Unset/empty is NOT ductless (prior
// regression lesson — never treat unset as a committed pathway); a future
// multi-select preference counts as selected ⊆ {Ductless Mini Split}
// (non-empty). Pure half so reactive selectors can evaluate a value.
export function isDuctlessOnlySystemPreference(value: unknown): boolean {
  if (Array.isArray(value)) {
    return (
      value.length > 0 &&
      value.every((entry) => entry === DUCTLESS_PATHWAY_SYSTEM_PREFERENCE)
    );
  }
  return value === DUCTLESS_PATHWAY_SYSTEM_PREFERENCE;
}

export function isDuctlessOnlyPreference() {
  return isDuctlessOnlySystemPreference(
    getProjectSettingValue('system_preference')
  );
}

export const HYBRID_PATHWAY_SYSTEM_PREFERENCE = 'Hybrid';

// STRICT hybrid commitment (AHA-6784) — exact mirror of
// isDuctlessOnlySystemPreference. Unset/empty is NOT hybrid; a future
// multi-select preference counts as selected ⊆ {Hybrid} (non-empty).
// Pure half so reactive selectors can evaluate a value.
export function isHybridOnlySystemPreference(value: unknown): boolean {
  if (Array.isArray(value)) {
    return (
      value.length > 0 &&
      value.every((entry) => entry === HYBRID_PATHWAY_SYSTEM_PREFERENCE)
    );
  }
  return value === HYBRID_PATHWAY_SYSTEM_PREFERENCE;
}

export function isHybridOnlyPreference() {
  return isHybridOnlySystemPreference(
    getProjectSettingValue('system_preference')
  );
}

export function isDuctedOrHybridSystemPreference(value: unknown): boolean {
  return (
    isDuctedOnlySystemPreference(value) || isHybridOnlySystemPreference(value)
  );
}

export function isDuctedOrHybridPreference() {
  return isDuctedOrHybridSystemPreference(
    getProjectSettingValue('system_preference')
  );
}

export function isPtacPreference() {
  return isPtacSystemPreference(getProjectSettingValue('system_preference'));
}

// Pathways whose Layout & Sizing starts as the basic sizing estimate with a
// detailed-estimate opt-in (ducted, hybrid). PTAC runs the full room-by-room
// layout like ductless — every room gets its own unit.
export function isBasicSizingSystemPreference(value: unknown): boolean {
  return isDuctedOrHybridSystemPreference(value);
}

// Pathways whose Choose Systems runs the compatible-combinations pipeline
// off the existing-system wants (the ducted pathway) rather than the
// configuration-derived ductless flow. PTAC sizes its units per room off
// the System Design instead (features/ptac-selection).
export function isCompatibilitySystemPreference(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    DUCTED_PATHWAY_SYSTEM_PREFERENCES.includes(value)
  );
}

// Pathways whose System Design places outdoor condensers, so Layout & Sizing
// asks where they can go. Ducted systems place a single unit from the
// existing-equipment layout and PTAC units sit in the room's wall — neither
// asks.
export function pathwayHasOutdoorCondensers(value: unknown): boolean {
  return (
    !(
      typeof value === 'string' &&
      DUCTED_PATHWAY_SYSTEM_PREFERENCES.includes(value)
    ) && !isPtacSystemPreference(value)
  );
}

export function isDetailedSizingPreference(value: unknown): boolean {
  return value === 'Detailed Estimate';
}

export function selectIsDetailedSizingPreference(state: {
  project: { projectSettings: AttributeType[] };
}): boolean {
  return isDetailedSizingPreference(
    state.project.projectSettings.find(
      (setting) => setting.code === 'sizing_preference'
    )?.value
  );
}

// EP-only gate (AHA-6784): the existing equipment that applies to the
// project is Electrical-Panel-only, so the ductless-style flow governs —
// true for a committed ductless preference. Hybrid is NOT EP-only:
// existing ducted equipment is optional there. EP = Electrical Panel.
// Pure half + store-reading half, same split as the strict predicates
// above.
export function isEpOnlySystemPreference(value: unknown): boolean {
  return isDuctlessOnlySystemPreference(value);
}

export function isEpOnlyPreference() {
  return isEpOnlySystemPreference(getProjectSettingValue('system_preference'));
}

// The empty-comparison Back fallback (quote/accessories). System Design is
// LOCKED on the System Design bypass modes (utils/system-design-bypass:
// exclusively-ducted, or hybrid with no mini-split rooms), so falling back
// to it would strand the user on a locked tab — steer to Choose Systems
// instead, whose pathway-gated empty state routes on from there.
export function getEmptyComparisonBackTarget(
  systemDesignLocked: boolean
): 'chooseSystems' | 'configurations' {
  return systemDesignLocked ? 'chooseSystems' : 'configurations';
}

export type QuoteBackTargetLocks = {
  systemDesignLocked: boolean;
};

// Quote-page Back target. Quotable systems always go back to Add-Ons; a
// locked System Design only matters for the empty-comparison fallback.
export function getQuoteBackTarget(
  hasQuotableSystems: boolean,
  hasSystemsToCompare: boolean,
  { systemDesignLocked }: QuoteBackTargetLocks
): 'accessories' | 'chooseSystems' | 'configurations' {
  if (hasQuotableSystems) {
    return 'accessories';
  }
  if (hasSystemsToCompare) {
    return 'chooseSystems';
  }
  return getEmptyComparisonBackTarget(systemDesignLocked);
}

export function isDuctedSystemSelected(
  projectSettings: AttributeType[]
): boolean {
  return (
    projectSettings.find((ps) => ps.code === 'system_preference')?.value ===
    'Ducted'
  );
}
